<template>
    <div class="max-w-(--breakpoint-lg) flex flex-col gap-4">
        <page-header>Salesforce</page-header>

        <vx-expandable-card>
            <template v-slot:header>
                <div class="flex items-center">
                    <span>Salesforce Credentials</span>
                    <experimental-pill class="ml-2" type="alpha"></experimental-pill>
                </div>
            </template>
            <template v-slot:headerRight>
                <div v-if="hasPermission">
                    <vx-badge :color="authStatus === 'success' ? 'success' : 'slate'">
                        {{ authStatus === 'success' ? 'Connected' : 'Pending' }}
                    </vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <form
                    v-else
                    ref="urlForm"
                    class="flex flex-col gap-6 mt-3"
                    role="form"
                    :action="`/teams/${teamId}/integrations/salesforce/oauth`"
                    method="post"
                    @submit.prevent="createCredentials"
                >
                    <div>
                        <vx-label size="md">Base URL</vx-label>

                        <vx-input
                            type="text"
                            name="salesforce_domain"
                            placeholder="my-instance.my.salesforce.com"
                            v-model="salesforceDomain"
                            required
                        />
                    </div>

                    <vx-button type="submit" size="lg"> Authenticate </vx-button>
                </form>
            </template>
        </vx-expandable-card>

        <vx-expandable-card v-if="authStatus === 'success' && hasPermission">
            <template v-slot:header>
                <div class="flex items-center">
                    <span>Salesforce Field Mapping</span>
                    <experimental-pill class="ml-2" type="alpha"></experimental-pill>
                </div>
            </template>

            <template v-slot:content>
                <div class="flex flex-col gap-4">
                    <vx-table class="xl:grid-cols-4-auto" rowBreakpoint="xl">
                        <vx-table-row isHeader>
                            <vx-label size="sm" class="xl:col-span-2">Voxie Attribute</vx-label>
                            <vx-label size="sm">Salesforce Field</vx-label>
                            <vx-label size="sm">Source of Truth</vx-label>
                        </vx-table-row>

                        <vx-table-row>
                            <vx-table-row-item class="xl:col-span-2!">
                                <template v-slot:label> Voxie Attribute </template>
                                Phone
                            </vx-table-row-item>
                            <vx-table-row-item>
                                <template v-slot:label> Salesforce Field </template>
                                MobilePhone
                            </vx-table-row-item>
                            <vx-table-row-item>
                                <template v-slot:label> Source of Truth </template>
                                -
                            </vx-table-row-item>
                        </vx-table-row>

                        <vx-table-row
                            v-for="row in fixedRows"
                            :key="`salesforce-config__row-${row.id}`"
                            data-test="fields-fixed-rows"
                        >
                            <vx-table-row-item class="xl:col-span-2!" contentClasses="w-full">
                                <template v-slot:label> Voxie Attribute </template>
                                {{ fixedFieldLabels[row.key] }}
                            </vx-table-row-item>
                            <vx-table-row-item contentClasses="w-full">
                                <template v-slot:label> Salesforce Field </template>
                                <div class="w-full">
                                    <vx-input
                                        :model-value="row.value"
                                        @update:model-value="onChangeFixedInput({ id: row.id, value: $event })"
                                    />
                                </div>
                            </vx-table-row-item>
                            <vx-table-row-item contentClasses="w-full">
                                <template v-slot:label> Source of Truth </template>
                                <div class="flex items-center gap-3 w-full">
                                    <vx-selectable
                                        :model-value="row.source"
                                        :filterable="false"
                                        :options="syncTypeOptions"
                                        :clearable="false"
                                        :disabled="true"
                                        @update:model-value="onChangeFixedSource({ id: row.id, value: $event })"
                                    >
                                        <template v-slot:option="{ option }">
                                            <div v-if="option.label">{{ option.label }}</div>
                                        </template>

                                        <template v-slot:selected-option="{ option }">
                                            <div>{{ option.label }}</div>
                                        </template>
                                    </vx-selectable>
                                    <vx-button color="muted" disabled class="w-full sm:w-auto" outline>
                                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                                    </vx-button>
                                </div>
                            </vx-table-row-item>
                        </vx-table-row>

                        <vx-table-row
                            v-for="row in customAttributeRows"
                            :key="`salesforce-config__row-${row.id}`"
                            data-test="fields-custom-attribute-rows"
                        >
                            <vx-table-row-item class="xl:col-span-2!" contentClasses="w-full">
                                <template v-slot:label> Voxie Attribute </template>
                                <vx-selectable
                                    class="w-full"
                                    placeholder="Custom_Attribute"
                                    :model-value="row.key"
                                    :loading="loadingData"
                                    :filterable="false"
                                    :options="options"
                                    :clearable="false"
                                    @search="onSearch"
                                    @update:model-value="onChangeKey({ id: row.id, value: $event })"
                                >
                                    <template v-slot:no-options="{ search }">
                                        <div>
                                            <template v-if="search"> Sorry, no matching options. </template>

                                            <template v-else> Start typing to search field </template>
                                        </div>
                                    </template>

                                    <template v-slot:option="{ option }">
                                        <div v-if="option.label">{{ option.label }}</div>
                                    </template>

                                    <template v-slot:selected-option="{ option }">
                                        <div>{{ option.label }}</div>
                                    </template>
                                </vx-selectable>
                            </vx-table-row-item>
                            <vx-table-row-item contentClasses="w-full">
                                <template v-slot:label> Salesforce Field </template>
                                <div class="w-full">
                                    <vx-input
                                        :model-value="row.value"
                                        @update:model-value="
                                            onChangeCustomAttributeInput({ id: row.id, value: $event })
                                        "
                                    />
                                </div>
                            </vx-table-row-item>
                            <vx-table-row-item contentClasses="w-full">
                                <template v-slot:label> Source of Truth </template>
                                <div class="flex items-center gap-3 w-full">
                                    <vx-selectable
                                        class="grow w-full"
                                        :model-value="row.source"
                                        :filterable="false"
                                        :options="syncTypeOptions"
                                        :clearable="false"
                                        @update:model-value="
                                            onChangeCustomAttributeSource({ id: row.id, value: $event })
                                        "
                                    >
                                        <template v-slot:option="{ option }">
                                            <div v-if="option.label">{{ option.label }}</div>
                                        </template>

                                        <template v-slot:selected-option="{ option }">
                                            <div>{{ option.label }}</div>
                                        </template>
                                    </vx-selectable>
                                    <vx-button
                                        @click.prevent="removeRow(row.id)"
                                        color="muted"
                                        class="w-full sm:w-auto"
                                        outline
                                    >
                                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                                    </vx-button>
                                </div>
                            </vx-table-row-item>
                        </vx-table-row>
                    </vx-table>
                    <vx-box class="max-w-fit p-3">
                        <vx-button size="lg" @click="addNewCustomAttributeRow" data-test="add-button">
                            Add <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </vx-box>
                    <vx-button size="lg" block @click="saveConfig" data-test="save-button"> Save </vx-button>
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
    VxExpandableCard,
    VxSelectable,
    VxInput,
    VxLabel,
    VxButton,
    VxBadge,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxBox,
} from '@voxie/frontend-components';
import debounce from '~/utils/debounce';
import incrementingId from '../../../utils/incrementingId';
import { customAttributesClient, salesforceClient } from '~/services';
import ExperimentalPill from '~/components/general/ExperimentalPill.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { useToasted } from '~/composables/useToasted';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const hasPermission = computed(() => props.ownsTeam);

const salesforceDomain = ref('');
const urlForm = ref(null);
const teamId = Spark.state.currentTeam.id;
const loadingData = ref(false);
const isNew = ref(true);
const customAttributeRows = ref([]);
const fixedRows = ref([]);
const authStatus = ref('pending');
const options = ref([]);
const syncTypeOptions = [
    {
        value: 'voxie',
        label: 'Voxie',
    },
    {
        value: 'salesforce',
        label: 'Salesforce',
    },
];
const fixedFieldLabels = {
    first_name_mapping: 'First Name',
    last_name_mapping: 'Last Name',
    email_mapping: 'Email',
};

const createCredentials = () => {
    if (!validateUrl()) {
        return;
    }

    urlForm.value.submit();
};

const validateUrl = () => {
    const errors = [];

    if ('' === salesforceDomain.value) {
        errors.push('Salesforce domain field is required!');
    }

    if (salesforceDomain.value.startsWith('http://') || salesforceDomain.value.startsWith('https://')) {
        errors.push('Salesforce domain field should not contain http:// or https://!');
    }

    if (!salesforceDomain.value.endsWith('.my.salesforce.com')) {
        errors.push('Salesforce domain field should end with .my.salesforce.com!');
    }

    if (errors.length === 0) {
        return true;
    }

    const errorMessage = `<p>We found the following errors:</p><br/>${errors.join('<br/>')}`;

    VxSwal.fire({
        title: 'Oops..',
        html: errorMessage,
        icon: 'error',
    });

    return false;
};

const getSalesforceCredentials = async () => {
    try {
        const { data } = await salesforceClient.getCredentials(teamId);

        if (data.access_token) {
            authStatus.value = 'success';
            salesforceDomain.value = data.salesforce_domain;
        }

        await getConfig();
    } catch {
        authStatus.value = 'pending';
    }
};

const getConfig = async () => {
    const source = { value: 'salesforce', label: 'Salesforce' };

    try {
        const { data } = await salesforceClient.getConfig(teamId);
        isNew.value = false;

        addNewFixedRow('first_name_mapping', data.first_name_mapping.attribute, source);
        addNewFixedRow('last_name_mapping', data.last_name_mapping.attribute, source);
        addNewFixedRow('email_mapping', data.email_mapping.attribute, source);

        if (data?.custom_attribute_mappings) {
            Object.keys(data.custom_attribute_mappings).forEach((key) => {
                customAttributeRows.value.push({
                    id: incrementingId(),
                    key,
                    value: data.custom_attribute_mappings[key].attribute,
                    source: {
                        value: data.custom_attribute_mappings[key].source,
                        label: syncTypeOptions.find(
                            (option) => option.value === data.custom_attribute_mappings[key].source
                        ).label,
                    },
                });
            });
        }
    } catch {
        isNew.value = true;

        addNewFixedRow('first_name_mapping', '', source);
        addNewFixedRow('last_name_mapping', '', source);
        addNewFixedRow('email_mapping', '', source);
    } finally {
        if (customAttributeRows.value.length === 0) {
            addNewCustomAttributeRow();
        }
    }
};

const saveConfig = () => {
    const config = {
        first_name_mapping: { attribute: fixedRows.value[0].value, source: fixedRows.value[0].source.value },
        last_name_mapping: { attribute: fixedRows.value[1].value, source: fixedRows.value[1].source.value },
        email_mapping: { attribute: fixedRows.value[2].value, source: fixedRows.value[2].source.value },
    };

    customAttributeRows.value.forEach((row) => {
        if (row.key) {
            if (!config.custom_attribute_mappings) {
                config.custom_attribute_mappings = {};
            }

            config.custom_attribute_mappings[row.key] = { attribute: row.value, source: row.source.value };
        }
    });

    if (isNew.value) {
        createConfig(config);
    } else {
        updateConfig(config);
    }
};

const createConfig = async (config) => {
    await salesforceClient
        .createConfig(teamId, config)
        .then(() => {
            VxSwal.fire({
                title: 'Success!',
                text: 'Salesforce config has successfully been created',
                icon: 'success',
            });

            isNew.value = false;
        })
        .catch((err) => {
            showValidationErrors(err);
        });
};

const updateConfig = async (config) => {
    await salesforceClient
        .updateConfig(teamId, config)
        .then(() => {
            VxSwal.fire({
                title: 'Success!',
                text: 'Salesforce config has successfully been updated',
                icon: 'success',
            });

            isNew.value = false;
        })
        .catch((err) => {
            showValidationErrors(err);
        });
};

const addNewFixedRow = (key, value, source) => {
    fixedRows.value.push({
        id: incrementingId(),
        key,
        value,
        source,
    });
};

const addNewCustomAttributeRow = (
    key = null,
    value = null,
    source = {
        value: 'voxie',
        label: 'Voxie',
    }
) => {
    customAttributeRows.value.push({
        id: incrementingId(),
        key,
        value,
        source,
    });
};

const removeRow = (id) => {
    const index = customAttributeRows.value.findIndex((row) => row.id === id);

    if (index === -1) {
        return;
    }

    customAttributeRows.value.splice(index, 1);

    if (customAttributeRows.value.length === 0) {
        addNewCustomAttributeRow();
    }
};

const onChangeFixedSource = ({ id, value }) => {
    const index = fixedRows.value.findIndex((row) => row.id === id);

    if (index === -1) {
        return;
    }

    fixedRows.value[index].source = value;
};

const onChangeCustomAttributeSource = ({ id, value }) => {
    const index = customAttributeRows.value.findIndex((row) => row.id === id);

    if (index === -1) {
        return;
    }

    customAttributeRows.value[index].source = value;
};

const onChangeKey = ({ id, value }) => {
    const index = customAttributeRows.value.findIndex((row) => row.id === id);

    if (index === -1) {
        return;
    }

    customAttributeRows.value[index].key = value;
};

const onChangeFixedInput = ({ id, value }) => {
    const index = fixedRows.value.findIndex((row) => row.id === id);
    if (index === -1) {
        return;
    }
    fixedRows.value[index].value = value;
};
const onChangeCustomAttributeInput = ({ id, value }) => {
    const index = customAttributeRows.value.findIndex((row) => row.id === id);

    if (index === -1) {
        return;
    }

    customAttributeRows.value[index].value = value;
};

const runSearch = (loading, query) => {
    const params = query ? { query } : null;

    customAttributesClient
        .customAttributesAutocomplete(teamId, params)
        .then((response) => {
            if (response.data.data.length > 0) {
                options.value = response.data.data.map((value) => value.toString());
                loading(false);
            }
        })
        .catch((error) => {
            console.error(error);
            toasted.global.platform_error();
        })
        .finally(() => {
            loading(false);
        });
};

const debouncedSearch = debounce(runSearch, 350);

const onSearch = (search, loading) => {
    loading(true);
    debouncedSearch(loading, search);
};

const showValidationErrors = (err) => {
    const errorMessage = catchRequestError(err);

    VxSwal.fire({
        title: 'Oops..',
        html: errorMessage,
        icon: 'error',
    });
};

if (hasPermission.value) {
    getSalesforceCredentials();
}
</script>
